import { logger } from 'common/services/logger';

import {
  type AccountlessNatalieEventApiResponse,
  type NatalieEventDetail,
  type NatalieEventDetailData,
  type NatalieEventPublicDetail,
  type NatalieEventPublicDetailData,
  type ExploreUseCase,
  type Persona,
  NatalieEventStatus,
} from './natalieEvent.types';

const isNatalieEventStatus = (value: string): value is NatalieEventStatus =>
  value === NatalieEventStatus.Active || value === NatalieEventStatus.Completed;

/** The raw stored status an older accountless deployment reports for an ended
 *  event. The API now maps this to 'COMPLETED' itself (natalie-api#118), matching
 *  the JWT-authenticated endpoints, so this is back-compat only. */
const ACCOUNTLESS_DB_CLOSED = 'CLOSED';

/**
 * Narrow the accountless status onto the ACTIVE | COMPLETED domain type.
 *
 * The 'CLOSED' branch is deliberately kept even though the API no longer sends it:
 * this bundle can be live before the API deploy lands, and the fallback below is
 * fail-open (Active), which would put a live join CTA on an ended conversation.
 * Mapping it costs one comparison and makes the two deploys order-independent.
 */
const narrowAccountlessStatus = (value: string): NatalieEventStatus => {
  if (isNatalieEventStatus(value)) return value;
  if (value === ACCOUNTLESS_DB_CLOSED) return NatalieEventStatus.Completed;
  logger.warn(`[mapAccountlessEventFromApi] Unknown status "${value}", defaulting to Active`);
  return NatalieEventStatus.Active;
};

// Default a missing/legacy persona (events created before the field existed) to
// { type: 'user' }, and defend the boundary against any unexpected runtime value.
const narrowPersona = (persona: Persona | null | undefined): Persona => {
  if (persona?.type === 'user' || persona?.type === 'buyer') return { type: persona.type };
  if (persona !== undefined && persona !== null) {
    logger.warn('[natalie mappers] Unexpected persona, defaulting to user:', { persona });
  }
  return { type: 'user' };
};

// Default a missing/legacy exploreUseCases (events created before the field existed,
// or non-array runtime values) to []. The backend is authoritative on card content,
// so this is a light shape guard: pass through well-formed { icon, title, body }
// objects and drop malformed entries defensively rather than enforce an allowlist.
const isWellFormedExploreUseCase = (item: unknown): item is ExploreUseCase => {
  if (typeof item !== 'object' || item === null) return false;
  const candidate = item as Record<string, unknown>;
  return (
    typeof candidate.icon === 'string' && typeof candidate.title === 'string' && typeof candidate.body === 'string'
  );
};

const narrowExploreUseCases = (exploreUseCases: ExploreUseCase[] | null | undefined): ExploreUseCase[] => {
  if (Array.isArray(exploreUseCases)) {
    const wellFormed = exploreUseCases.filter(isWellFormedExploreUseCase);
    if (wellFormed.length !== exploreUseCases.length) {
      logger.warn('[natalie mappers] Dropped malformed exploreUseCases entries:', {
        received: exploreUseCases.length,
        kept: wellFormed.length,
      });
    }
    return wellFormed;
  }
  if (exploreUseCases !== undefined && exploreUseCases !== null) {
    logger.warn('[natalie mappers] Unexpected exploreUseCases, defaulting to []:', { exploreUseCases });
  }
  return [];
};

export const mapNataliePublicEventDetailFromApi = (data: NatalieEventPublicDetailData): NatalieEventPublicDetail => ({
  id: data.id,
  communityId: data.community.id,
  endDate: data.endDate,
  name: data.name,
  topic: data.topic,
  description: data.description ?? null,
  ownerName: data.ownerName,
  communityName: data.community.name ?? '',
  status: data.status,
  communityLogo: data.community.logoImageLink ?? null,
  videoUrl: data.videoUrl ?? null,
  durationInMinutes: data.sessionDurationMinutes ?? 0,
  persona: narrowPersona(data.persona),
  exploreUseCases: narrowExploreUseCases(data.exploreUseCases),
  testInviteCode: data.testInviteCode ?? null,
});

export const mapNatalieEventDetailFromApi = (data: NatalieEventDetailData): NatalieEventDetail => ({
  id: data.id,
  communityId: data.community.id,
  endDate: data.endDate,
  name: data.name,
  topic: data.topic,
  description: data.description ?? null,
  ownerName: data.ownerName,
  communityName: data.community.name ?? '',
  status: data.status,
  communityLogo: data.community.logoImageLink ?? null,
  videoUrl: data.videoUrl ?? null,
  durationInMinutes: data.sessionDurationMinutes ?? 0,
  persona: narrowPersona(data.persona),
  exploreUseCases: narrowExploreUseCases(data.exploreUseCases),
  testInviteCode: data.testInviteCode ?? null,
});

export const mapAccountlessEventFromApi = (data: AccountlessNatalieEventApiResponse): NatalieEventPublicDetail => ({
  id: data.id,
  communityId: data.community.id,
  endDate: data.endDate,
  name: data.name,
  topic: data.topic,
  description: data.description ?? null,
  ownerName: data.ownerName ?? '',
  communityName: data.community.name ?? '',
  // Accountless OpenAPI types `status` as plain string. Narrow at the boundary so an
  // unknown future value defaults to Active (and surfaces in dev/staging logs)
  // rather than silently flowing through.
  status: narrowAccountlessStatus(data.status),
  durationInMinutes: data.sessionDurationMinutes ?? 0,
  communityLogo: data.community.logoImageLink ?? null,
  videoUrl: null,
  persona: narrowPersona(data.persona),
  exploreUseCases: narrowExploreUseCases(data.exploreUseCases),
  testInviteCode: data.testInviteCode ?? null,
});
